/**
 * Client-side invoice (Kleinbetragsrechnung §33 UStDV) — zero dependency.
 *
 * Builds a print-optimised, light-theme HTML invoice from order data the
 * dashboard already has + static Anbieter details, and opens it in a new tab
 * (fallback: hidden-iframe auto-print) so the customer can "Save as PDF" via
 * the browser. No backend call and no PDF library needed.
 *
 * David is a Kleinunternehmer (§19 UStG) → no VAT is shown; a note explains it.
 * As a Kleinbetragsrechnung the buyer's postal address is not required.
 *
 * NOTE: verify the SELLER block below (address / optional Steuernummer) — it is
 * the David Media Anbieter identity used across Impressum/AGB.
 */

const SELLER = {
  brand: 'DAVIDMEDIA',
  name: 'David Ferfers',
  street: 'Bruchhöfe 21',
  city: '47829 Krefeld',
  email: 'contact@davidmedia.de',
};

const esc = (s) =>
  String(s == null ? '' : s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

const fmtMoney = (n, ccy = 'eur') =>
  `${(Number(n) || 0).toFixed(2)} ${String(ccy || 'eur').toUpperCase() === 'EUR' ? '€' : String(ccy || '').toUpperCase()}`;

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' }); }
  catch { return iso || ''; }
};

export function buildInvoiceHTML(order, buyer) {
  const nr = order.order_number || '—';
  const amount = fmtMoney(order.amount, order.currency);
  const date = fmtDate(order.created_at);
  const buyerName = buyer?.name || buyer?.email || 'Kunde';
  return `<!doctype html><html lang="de"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><title>Rechnung ${esc(nr)}</title>
<style>
  *{box-sizing:border-box}
  body{font-family:-apple-system,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;color:#1a1a1a;margin:0;padding:40px;background:#f4f4f5}
  .sheet{max-width:720px;margin:0 auto;background:#fff;padding:48px;box-shadow:0 6px 30px rgba(0,0,0,.08);border-radius:6px}
  .top{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #F97316;padding-bottom:20px}
  .brand{font-size:22px;font-weight:800;letter-spacing:2px}
  .brand small{display:block;font-size:10px;font-weight:600;letter-spacing:3px;color:#F97316;margin-top:3px}
  h1{font-size:26px;margin:0;font-weight:700}
  .muted{color:#666;font-size:13px;line-height:1.55}
  .cols{display:flex;justify-content:space-between;gap:24px;margin-top:28px}
  .label{font-size:10px;text-transform:uppercase;letter-spacing:1.5px;color:#999;margin-bottom:6px}
  table{width:100%;border-collapse:collapse;margin-top:34px}
  th{text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:1px;color:#999;border-bottom:1px solid #ddd;padding:8px 0}
  td{padding:14px 0;border-bottom:1px solid #eee;font-size:14px}
  .num{text-align:right}
  .total{display:flex;justify-content:flex-end;margin-top:18px}
  .total .box{min-width:240px}
  .total .row{display:flex;justify-content:space-between;padding:6px 0;font-size:14px}
  .total .grand{font-size:18px;font-weight:800;border-top:2px solid #1a1a1a;margin-top:4px;padding-top:10px}
  .paid{display:inline-block;margin-top:8px;font-size:11px;font-weight:700;color:#0a7a4b;border:1px solid #0a7a4b;border-radius:999px;padding:2px 10px}
  .note{margin-top:34px;font-size:12px;color:#555;line-height:1.6;border-top:1px solid #eee;padding-top:18px}
  .foot{margin-top:32px;font-size:11px;color:#aaa;text-align:center;line-height:1.6}
  .btnbar{max-width:720px;margin:0 auto 20px;text-align:right}
  .btn{background:#F97316;color:#fff;border:0;border-radius:8px;padding:10px 22px;font-size:13px;font-weight:600;cursor:pointer}
  @media print{body{background:#fff;padding:0}.sheet{box-shadow:none;border-radius:0;padding:24px;max-width:none}.btnbar,.noprint{display:none!important}}
</style></head><body>
  <div class="btnbar noprint"><button class="btn" onclick="window.print()">Als PDF speichern / Drucken</button></div>
  <div class="sheet">
    <div class="top">
      <div class="brand">${esc(SELLER.name)}</div>
      <div style="text-align:right"><h1>Rechnung</h1><div class="muted">Nr. ${esc(nr)}<br>${esc(date)}<br><span class="paid">Bezahlt</span></div></div>
    </div>
    <div class="cols">
      <div>
        <div class="label">Anbieter</div>
        <div class="muted"><strong>${esc(SELLER.name)}</strong><br>${esc(SELLER.street)}<br>${esc(SELLER.city)}<br>${esc(SELLER.email)}</div>
      </div>
      <div style="text-align:right">
        <div class="label">Rechnung an</div>
        <div class="muted"><strong>${esc(buyerName)}</strong>${(buyer?.email && buyer.email !== buyerName) ? '<br>' + esc(buyer.email) : ''}${buyer?.customer_number ? '<br>Kundennr. ' + esc(buyer.customer_number) : ''}</div>
      </div>
    </div>
    <table>
      <thead><tr><th>Beschreibung</th><th class="num">Menge</th><th class="num">Betrag</th></tr></thead>
      <tbody><tr><td>${esc(order.product_name || 'Produkt')}</td><td class="num">1</td><td class="num">${esc(amount)}</td></tr></tbody>
    </table>
    <div class="total"><div class="box">
      <div class="row"><span class="muted">Zwischensumme</span><span>${esc(amount)}</span></div>
      <div class="row grand"><span>Gesamt</span><span>${esc(amount)}</span></div>
    </div></div>
    <div class="note">Gemäß § 19 UStG (Kleinunternehmerregelung) wird keine Umsatzsteuer berechnet und daher nicht gesondert ausgewiesen.<br>Vielen Dank für deinen Einkauf.</div>
    <div class="foot">${esc(SELLER.name)} · ${esc(SELLER.street)}, ${esc(SELLER.city)} · ${esc(SELLER.email)}</div>
  </div>
</body></html>`;
}

/* ============================================================================
 * FREE-FORM INVOICE (e.g. video jobs) — a FULL invoice, not just a
 * Kleinbetragsrechnung: buyer address, own invoice number, service date,
 * multiple line items, payment terms + bank details (these jobs aren't
 * pre-paid via Stripe, so the money still has to arrive). Still §19 → no VAT.
 * ========================================================================== */

const money = (n) => `${(Number(n) || 0).toFixed(2).replace('.', ',')} €`;

const dateDE = (d) => {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
  catch { return d; }
};

export function lineTotal(item) {
  return (Number(item?.qty) || 0) * (Number(item?.unitPrice) || 0);
}

export function invoiceTotal(items) {
  return (items || []).reduce((sum, i) => sum + lineTotal(i), 0);
}

export function buildCustomInvoiceHTML(d = {}) {
  const items = (d.items || []).filter(
    (i) => (i.description || '').trim() !== '' || Number(i.unitPrice) || Number(i.qty),
  );
  const total = invoiceTotal(items);
  const b = d.buyer || {};
  const bank = d.bank || {};
  const buyerLines = [b.company, b.name, b.street, b.zipCity, b.email].filter((x) => (x || '').trim());
  const rows = items.length
    ? items.map((i) => `<tr>
        <td>${esc(i.description || '—')}</td>
        <td class="num">${esc(String(i.qty ?? '').replace('.', ','))}</td>
        <td class="num">${esc(money(i.unitPrice))}</td>
        <td class="num">${esc(money(lineTotal(i)))}</td>
      </tr>`).join('')
    : `<tr><td colspan="4" style="color:#bbb">Noch keine Positionen</td></tr>`;

  const bankBlock = (bank.iban || bank.holder || bank.bic)
    ? `<div class="pay"><div class="label">Zahlung</div><div class="muted">
        ${d.paymentTerms ? esc(d.paymentTerms) + '<br>' : ''}
        ${bank.holder ? 'Kontoinhaber: ' + esc(bank.holder) + '<br>' : ''}
        ${bank.iban ? 'IBAN: ' + esc(bank.iban) + '<br>' : ''}
        ${bank.bic ? 'BIC: ' + esc(bank.bic) : ''}
      </div></div>`
    : (d.paymentTerms ? `<div class="pay"><div class="label">Zahlung</div><div class="muted">${esc(d.paymentTerms)}</div></div>` : '');

  return `<!doctype html><html lang="de"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><title>Rechnung ${esc(d.invoiceNumber || '')}</title>
<style>
  *{box-sizing:border-box}
  body{font-family:-apple-system,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;color:#1a1a1a;margin:0;padding:32px;background:#f4f4f5}
  .sheet{max-width:720px;margin:0 auto;background:#fff;padding:48px;box-shadow:0 6px 30px rgba(0,0,0,.08);border-radius:6px}
  .top{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #F97316;padding-bottom:20px}
  .brand{font-size:22px;font-weight:800;letter-spacing:2px}
  .brand small{display:block;font-size:10px;font-weight:600;letter-spacing:3px;color:#F97316;margin-top:3px}
  h1{font-size:26px;margin:0;font-weight:700}
  .muted{color:#666;font-size:13px;line-height:1.55}
  .cols{display:flex;justify-content:space-between;gap:24px;margin-top:28px}
  .label{font-size:10px;text-transform:uppercase;letter-spacing:1.5px;color:#999;margin-bottom:6px}
  table{width:100%;border-collapse:collapse;margin-top:34px}
  th{text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:1px;color:#999;border-bottom:1px solid #ddd;padding:8px 0}
  td{padding:12px 0;border-bottom:1px solid #eee;font-size:14px;vertical-align:top}
  .num{text-align:right;white-space:nowrap}
  .total{display:flex;justify-content:flex-end;margin-top:18px}
  .total .box{min-width:260px}
  .total .row{display:flex;justify-content:space-between;padding:6px 0;font-size:14px}
  .total .grand{font-size:18px;font-weight:800;border-top:2px solid #1a1a1a;margin-top:4px;padding-top:10px}
  .pay{margin-top:28px;border-top:1px solid #eee;padding-top:16px}
  .note{margin-top:20px;font-size:12px;color:#555;line-height:1.6}
  .foot{margin-top:30px;font-size:11px;color:#aaa;text-align:center;line-height:1.6}
  .btnbar{max-width:720px;margin:0 auto 20px;text-align:right}
  .btn{background:#F97316;color:#fff;border:0;border-radius:8px;padding:10px 22px;font-size:13px;font-weight:600;cursor:pointer}
  @media print{body{background:#fff;padding:0}.sheet{box-shadow:none;border-radius:0;padding:24px;max-width:none}.btnbar,.noprint{display:none!important}}
</style></head><body>
  ${d.embedded ? '' : '<div class="btnbar noprint"><button class="btn" onclick="window.print()">Als PDF speichern / Drucken</button></div>'}
  <div class="sheet">
    <div class="top">
      <div class="brand">${esc(SELLER.name)}</div>
      <div style="text-align:right"><h1>Rechnung</h1><div class="muted">
        ${d.invoiceNumber ? 'Nr. ' + esc(d.invoiceNumber) + '<br>' : ''}
        ${d.invoiceDate ? 'Datum: ' + esc(dateDE(d.invoiceDate)) + '<br>' : ''}
        ${d.serviceDate ? 'Leistung: ' + esc(d.serviceDate) : ''}
      </div></div>
    </div>
    <div class="cols">
      <div>
        <div class="label">Anbieter</div>
        <div class="muted"><strong>${esc(SELLER.name)}</strong><br>${esc(SELLER.street)}<br>${esc(SELLER.city)}<br>${esc(SELLER.email)}${d.taxNumber ? '<br>Steuernr. ' + esc(d.taxNumber) : ''}</div>
      </div>
      <div style="text-align:right">
        <div class="label">Rechnung an</div>
        <div class="muted">${buyerLines.length ? buyerLines.map((l, i) => (i === 0 ? '<strong>' + esc(l) + '</strong>' : esc(l))).join('<br>') : '<span style="color:#bbb">Empfänger noch leer</span>'}</div>
      </div>
    </div>
    <table>
      <thead><tr><th>Beschreibung</th><th class="num">Menge</th><th class="num">Einzelpreis</th><th class="num">Gesamt</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>
    <div class="total"><div class="box">
      <div class="row"><span class="muted">Zwischensumme</span><span>${esc(money(total))}</span></div>
      <div class="row grand"><span>Gesamt</span><span>${esc(money(total))}</span></div>
    </div></div>
    ${bankBlock}
    <div class="note">Gemäß § 19 UStG (Kleinunternehmerregelung) wird keine Umsatzsteuer berechnet und daher nicht gesondert ausgewiesen.${d.note ? '<br>' + esc(d.note) : ''}</div>
    <div class="foot">${esc(SELLER.name)} · ${esc(SELLER.street)}, ${esc(SELLER.city)} · ${esc(SELLER.email)}</div>
  </div>
</body></html>`;
}

/** Open a free-form invoice in a new tab. */
export function openCustomInvoice(data) {
  const html = buildCustomInvoiceHTML(data);
  let w = null;
  try { w = window.open('', '_blank'); } catch { w = null; }
  if (w && w.document) {
    w.document.open(); w.document.write(html); w.document.close();
  }
}

/** Open the invoice in a new tab (fallback: hidden-iframe auto-print). */
export function openInvoice(order, buyer) {
  const html = buildInvoiceHTML(order, buyer);
  let w = null;
  try { w = window.open('', '_blank'); } catch { w = null; }
  if (w && w.document) {
    w.document.open();
    w.document.write(html);
    w.document.close();
    return;
  }
  // Popup blocked → render into a hidden iframe and trigger the print dialog.
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document;
  doc.open(); doc.write(html); doc.close();
  setTimeout(() => {
    try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch { /* ignore */ }
    setTimeout(() => { try { document.body.removeChild(iframe); } catch { /* ignore */ } }, 1500);
  }, 350);
}
